// hooks/useConversationsCache.ts — v1
//
// Cache centralisé des messages par conversation, tenu par MessagesScreen
// (ou tout écran parent qui monte/démonte ChatBox). Objectif : quand
// l'utilisateur quitte une conversation puis y revient, ChatBox peut être
// réhydraté INSTANTANÉMENT depuis ce cache (affichage immédiat, sans
// attendre le socket), pendant que le socket se reconnecte en arrière-plan
// pour resynchroniser silencieusement.
//
// Ce cache ne gère AUCUNE connexion socket — c'est uniquement un stockage
// en mémoire de `Message[]` par conversationId, avec une éviction LRU pour
// éviter une croissance illimitée en session longue (beaucoup de
// conversations visitées).
//
// Limite volontaire : MAX_CACHED_CONVERSATIONS. Au-delà, la conversation la
// moins récemment consultée est évincée du cache (elle rechargera depuis la
// DB au prochain accès, comportement actuel inchangé pour ce cas).

import { useRef, useCallback } from 'react';

// ─── Type partagé Message ──────────────────────────────────────────────────
// Source de vérité unique — ChatBoxTypes.ts réexporte ce type pour que
// ChatBoxWeb.tsx / ChatBoxMobile.tsx / MessagesScreen.tsx utilisent tous
// exactement la même forme (évite les divergences observées entre les
// définitions locales de ChatBoxWeb et ChatBoxMobile).

export interface ReplyTo {
  id:            string;
  sender_id:     string;
  content:       string | null;
  sender_nom:    string;
  sender_prenom: string;
}

export interface Message {
  id:                string;
  content:           string | null;
  media_url:         string | null;
  senderId:          string;
  isFromMe:          boolean;
  senderName:        string;
  senderAvatar:      string | null;
  createdAt:         string;
  isRead?:           boolean;
  deletedAt?:        string | null;
  editedAt?:         string | null;
  reply_to_id?:      string | null;
  is_visible?:       boolean;
  is_private_reply?: boolean;
  replyTo?:          ReplyTo | null;
  mediaType?:        string | null;
  _tempId?:          string;
  _pending?:         boolean;
  _failed?:          boolean;
}

// ─── Config ─────────────────────────────────────────────────────────────────

const MAX_CACHED_CONVERSATIONS = 15;

interface CacheEntry {
  messages:   Message[];
  hasMore:    boolean;
  lastAccess: number;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useConversationsCache() {
  // useRef (pas useState) : le cache ne doit JAMAIS déclencher de re-render
  // du parent à lui seul — seule la lecture ponctuelle lors de l'ouverture
  // d'une conversation nous intéresse, pas la réactivité continue.
  const cacheRef = useRef<Map<string, CacheEntry>>(new Map());

  /** Lit le cache pour une conversation. Retourne undefined si absent. */
  const getCached = useCallback((conversationId: string): CacheEntry | undefined => {
    const entry = cacheRef.current.get(conversationId);
    if (entry) entry.lastAccess = Date.now();
    return entry;
  }, []);

  /** Écrit/actualise le cache pour une conversation, avec éviction LRU. */
  const setCached = useCallback((conversationId: string, messages: Message[], hasMore: boolean = false) => {
    const cache = cacheRef.current;
    cache.set(conversationId, { messages, hasMore, lastAccess: Date.now() });

    if (cache.size > MAX_CACHED_CONVERSATIONS) {
      let oldestId: string | null = null;
      let oldestTime = Infinity;
      for (const [id, entry] of cache.entries()) {
        if (entry.lastAccess < oldestTime) { oldestTime = entry.lastAccess; oldestId = id; }
      }
      if (oldestId) cache.delete(oldestId);
    }
  }, []);

  /** Supprime une conversation du cache (ex. amitié retirée, groupe quitté). */
  const invalidateCached = useCallback((conversationId: string) => {
    cacheRef.current.delete(conversationId);
  }, []);

  /** Vide entièrement le cache (ex. déconnexion utilisateur). */
  const clearCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  return { getCached, setCached, invalidateCached, clearCache };
}
